import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { Decoration } from '@atlaskit/editor-prosemirror/view';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { token } from '@atlaskit/tokens';

/**
 * We have a couple of nodes that require us to compute style
 * on different elements, ideally all nodes should be able to
 * compute the appropriate styles based on their wrapper.
 */
const nestedCases: Record<string, string> = {
	'tableView-content-wrap': 'table',
	'mediaSingleView-content-wrap': '.rich-media-item',
	'bodiedExtensionView-content-wrap': '.extension-container',
	'multiBodiedExtensionView-content-wrap': '.multiBodiedExtension--container',
	'embedCardView-content-wrap': '.rich-media-item',
	'datasourceView-content-wrap': '.datasourceView-content-inner-wrap',
};

const computeNestedStyle = (dom: HTMLElement) => {
	const foundKey = Object.keys(nestedCases).find((className) => dom.classList.contains(className));
	const nestedSelector = foundKey && nestedCases[foundKey];

	if (nestedSelector) {
		const nestedElement = dom.querySelector(nestedSelector);
		if (nestedElement) {
			return window.getComputedStyle(nestedElement);
		}
	}
};

const measureHeight = (style: CSSStyleDeclaration) => {
	return measureValue(style, [
		'height',
		'padding-top',
		'padding-bottom',
		'border-top-width',
		'border-bottom-width',
	]);
};

const measureValue = (style: CSSStyleDeclaration, measureValues: Array<string>) => {
	const [base, ...contentBoxValues] = measureValues;
	const measures = [style.getPropertyValue(base)];

	const boxSizing = style.getPropertyValue('box-sizing');
	if (boxSizing === 'content-box') {
		contentBoxValues.forEach((value) => {
			measures.push(style.getPropertyValue(value));
		});
	}

	let result = 0;
	for (let i = 0; i < measures.length; i++) {
		result += parseFloat(measures[i]);
	}
	return result;
};

function getLayoutModeFromTargetNode(node: PMNode): string {
	let layout;

	if (node.attrs.layout) {
		layout = node.attrs.layout;
	}

	if (node.marks && node.marks.length) {
		layout = (
			node.marks.find((mark) => mark.type.name === 'breakout') || {
				attrs: { mode: '' },
			}
		).attrs.mode;
	}

	if (node.type.name === 'table' && node.attrs.width) {
		layout = 'fixed-width';
	}

	if (['wide', 'full-width', 'fixed-width'].indexOf(layout) === -1) {
		return '';
	}

	return layout;
}

/**
 * Converts a camelCased CSS property name to a hyphenated CSS property name.
 *
 * @param property - CamelCased CSS property name.
 * @returns Hyphenated CSS property name.
 */
function hyphenate(property: string): string {
	// Ignored via go/ees005

	return property.replace(/[A-Z]/gu, (match) => `-${match.toLowerCase()}`).replace(/^ms/u, '-ms');
}

export const createBlockNodeConflictDecoration = (node: PMNode, pos: number): Decoration => {
	return Decoration.widget(
		pos,
		(view: EditorView, getPos: () => number | undefined) => {
			const nodeStart = getPos();
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			const element = document.createElement('span');
			element.className = 'conflictmarker';
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			element.appendChild(document.createElement('span'));
			const dom = nodeStart !== undefined ? view.nodeDOM(nodeStart) : null;

			if (dom instanceof HTMLElement && element.firstChild) {
				const style = computeNestedStyle(dom) || window.getComputedStyle(dom);

				const conflictmarker = element.firstChild as HTMLSpanElement;
				conflictmarker.style.height = `${measureHeight(style)}px`;

				const layoutMode = node && getLayoutModeFromTargetNode(node);

				if (nodeStart !== 0 || layoutMode || node?.type.name === 'table') {
					conflictmarker.style.marginTop = style.getPropertyValue('margin-top');
				}

				const conflictMarkerCursorStyles = {
					content: "''",
					position: 'absolute',
					background: token('color.background.accent.red.bolder'),
					width: token('space.075'),
					marginLeft: token('space.negative.075'),
					borderRadius: `${token('radius.small')} 0 0 ${token('radius.small')}`,
				};

				for (const [rule, value] of Object.entries(conflictMarkerCursorStyles)) {
					conflictmarker.style.setProperty(hyphenate(rule), value);
				}
				conflictmarker.className = 'conflictmarker';
				conflictmarker.setAttribute('data-testid', 'conflictmarker');
			}

			return element;
		},
		{ side: -1 },
	);
};
